import { useEffect, useState } from 'react';
import QRCode from 'qrcode';
import { toast } from 'sonner';
import { Copy, Check, Printer, LinkSimple, DownloadSimple } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { openPrintWindow } from '@/lib/printWindow';

// データ収集フォームの共有 — 技術アプリを開かせずに回答ページ (/f/{fid}) へ直接誘導。
export const EngFormShareDialog = ({ open, onClose, form }) => {
  const [qr, setQr] = useState('');
  const [copied, setCopied] = useState(false);
  const url = form ? `${window.location.origin}/f/${form.id}` : '';

  useEffect(() => {
    if (!open || !form) return;
    setCopied(false);
    QRCode.toDataURL(url, { width: 480, margin: 1 }).then(setQr).catch(() => {});
  }, [open, form, url]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success('リンクをコピーしました');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('コピーできませんでした。リンクを長押し/選択してコピーしてください');
    }
  };

  // デスクトップ用ショートカット (.html) — ダブルクリックで回答ページを開く自己完結ファイル
  const downloadShortcut = async () => {
    try {
      const iconB64 = await fetch('/icons/form-shortcut.png')
        .then((r) => r.blob())
        .then((b) => new Promise((res, rej) => {
          const fr = new FileReader();
          fr.onload = () => res(fr.result);
          fr.onerror = rej;
          fr.readAsDataURL(b);
        }));
      const safeTitle = (form.title || 'フォーム').replace(/</g, '&lt;');
      const html = `<!DOCTYPE html>
<html lang="ja"><head><meta charset="utf-8">
<title>${safeTitle} — 回答</title>
<link rel="icon" href="${iconB64}">
<meta http-equiv="refresh" content="2;url=${url}">
<style>
  body { margin:0; min-height:100vh; display:flex; align-items:center; justify-content:center;
    font-family:'Hiragino Sans','Yu Gothic',sans-serif; background:linear-gradient(135deg,#042f2e,#0e7490); }
  .card { text-align:center; color:#fff; padding:40px; animation:in .5s ease; }
  @keyframes in { from { opacity:0; transform:translateY(12px);} to { opacity:1; transform:none;} }
  img.icon { width:104px; height:104px; border-radius:24px; box-shadow:0 12px 40px rgba(0,0,0,.45); }
  h1 { font-size:19px; margin:20px 0 6px; font-weight:700; }
  p { font-size:13px; color:rgba(255,255,255,.75); margin:0 0 24px; }
  a.btn { display:inline-block; background:#14b8a6; color:#fff; text-decoration:none;
    padding:14px 34px; border-radius:999px; font-size:15px; font-weight:700;
    box-shadow:0 6px 20px rgba(20,184,166,.45); transition:transform .15s, background .15s; }
  a.btn:hover { background:#0d9488; transform:translateY(-1px); }
  .dots::after { content:''; animation:d 1.2s steps(4) infinite; }
  @keyframes d { 0%{content:''} 25%{content:'.'} 50%{content:'..'} 75%{content:'...'} }
</style></head>
<body><div class="card">
  <img class="icon" src="${iconB64}" alt="">
  <h1>${safeTitle}</h1>
  <p>回答ページを開いています<span class="dots"></span></p>
  <a class="btn" href="${url}">今すぐ開く</a>
</div>
<script>setTimeout(function(){ location.href = ${JSON.stringify(url)}; }, 1600);</script>
</body></html>`;
      const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `${(form.title || 'フォーム').replace(/[\\/:*?"<>|]/g, '_')}_回答.html`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(a.href), 4000);
      toast.success('ショートカットをダウンロードしました。デスクトップに置いてダブルクリックで開けます');
    } catch {
      toast.error('ショートカットの作成に失敗しました');
    }
  };

  const printQr = () => {
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>フォームQR ${form.title}</title>
<style>
  @page { size: A6 landscape; margin: 6mm; }
  body { font-family: sans-serif; margin: 0; display: flex; align-items: center; justify-content: center; height: 96vh; }
  .card { display: flex; align-items: center; gap: 8mm; }
  .qr { width: 55mm; height: 55mm; }
  .t { max-width: 70mm; }
  .title { font-size: 14pt; font-weight: bold; word-break: break-all; }
  .hint { font-size: 9pt; color: #444; margin-top: 3mm; line-height: 1.5; }
</style></head><body>
<div class="card">
  <img class="qr" src="${qr}" alt="QR" />
  <div class="t">
    <div class="title">${(form.title || '').replace(/</g, '&lt;')}</div>
    <div class="hint">スマホでこのQRを読み取ると<br/>回答ページが開きます。<br/>（社内アカウントでログイン）</div>
  </div>
</div>
</body></html>`;
    const w = openPrintWindow(html, { features: 'width=520,height=400' });
    if (!w) toast.error('ポップアップがブロックされました。ブラウザの設定でこのサイトのポップアップを許可してください');
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm" aria-describedby={undefined} data-testid="eng-share-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><LinkSimple size={18} className="text-teal-600" /> 回答リンクを共有</DialogTitle>
        </DialogHeader>
        {form && (
          <div className="space-y-4">
            <p className="text-xs text-zinc-500">このリンク/QRから、技術アプリを開かずに「{form.title}」の回答ページへ直接アクセスできます（要ログイン）。</p>
            {qr && (
              <div className="flex justify-center">
                <img src={qr} alt="フォームQR" className="w-44 h-44 rounded-xl border border-zinc-200 p-2 bg-white" data-testid="eng-share-qr" />
              </div>
            )}
            <div className="flex gap-2">
              <Input readOnly value={url} className="h-10 text-xs font-mono" onFocus={(e) => e.target.select()} data-testid="eng-share-url" />
              <Button onClick={copy} className="h-10 shrink-0 bg-teal-700 hover:bg-teal-800" data-testid="eng-share-copy">
                {copied ? <Check size={16} /> : <Copy size={16} />}
              </Button>
            </div>
            <div className="grid grid-cols-1 gap-2">
              <Button variant="outline" onClick={downloadShortcut} className="w-full h-10 border-teal-200 text-teal-700 hover:bg-teal-50" data-testid="eng-share-shortcut">
                <DownloadSimple size={16} className="mr-1.5" /> デスクトップ用ショートカット（.html）
              </Button>
              <Button variant="outline" onClick={printQr} className="w-full h-10 border-teal-200 text-teal-700 hover:bg-teal-50" data-testid="eng-share-print">
                <Printer size={16} className="mr-1.5" /> QRを印刷（機械に貼る用）
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
